import { ComposerBannerStack } from "./composer-notices/ComposerBannerStack";
import type { ComposerBannerStackItem } from "./composer-notices/ComposerBannerStack";
import { type ComponentProps, type ReactElement } from "react";
import { ChatComposer } from "./ChatComposer";
import { ComposerGitHubApprovalOverlay } from "./ComposerGitHubApprovalOverlay";
import { QueuedComposerMessages } from "./QueuedComposerMessages";
import type { QueuedComposerMessage } from "../../queuedMessageStore";
import { useComposerViewTransitionStyle } from "./composerViewTransition";
import { WorktreeLaunchPanel } from "./WorktreeLaunchPanel";

export type ComposerDockInput = ComponentProps<typeof ChatComposer> & {
  readonly runtimeAdmission?: import("./useRuntimeAdmission").RuntimeAdmissionState | null;
};
type GitHubApprovalProps = ComponentProps<typeof ComposerGitHubApprovalOverlay>;

/** Renders approvals, provider notices, queued follow-ups, and the active composer. */
export function ChatComposerDock(props: {
  readonly approval: {
    readonly confirmation: GitHubApprovalProps["confirmation"];
    readonly onApprove: GitHubApprovalProps["onApprove"];
    readonly onDecline: GitHubApprovalProps["onDecline"];
    readonly onSendFeedback: GitHubApprovalProps["onSendFeedback"];
    readonly onDismiss: () => void;
  } | null;
  readonly composerProps: ComposerDockInput;
  readonly usageNotice: ComposerBannerStackItem | null;
  readonly queuedMessages?: ReadonlyArray<QueuedComposerMessage> | undefined;
  readonly onSteerQueuedMessage?: ((id: string) => void) | undefined;
  readonly onRemoveQueuedMessage?: ((id: string) => void) | undefined;
}): ReactElement {
  const composerViewTransitionStyle = useComposerViewTransitionStyle();
  return (
    // Tagged for the floating browser preview: the player measures the dock's
    // height and the centered stack's columns to stay clear of the composer.
    <div data-chat-composer-dock="true" className="px-3 pt-1.5 pb-3 sm:px-5 sm:pt-2 sm:pb-4">
      {props.approval ? (
        <div className="mx-auto w-full min-w-0 max-w-3xl">
          <ComposerGitHubApprovalOverlay
            key={props.approval.confirmation.confirmationId}
            confirmation={props.approval.confirmation}
            onApprove={props.approval.onApprove}
            onSendFeedback={props.approval.onSendFeedback}
            onDismiss={props.approval.onDismiss}
            {...(props.approval.onDecline ? { onDecline: props.approval.onDecline } : {})}
          />
        </div>
      ) : (
        // Receives the morph from the new-thread hero. Tagged only on the plain
        // composer: while the GitHub approval overlay is showing, the composer
        // is not the element the user came from and morphing into the overlay
        // would animate the wrong box.
        <div
          data-chat-composer-stack="true"
          style={composerViewTransitionStyle}
          className="mx-auto w-full min-w-0 max-w-3xl"
        >
          <WorktreeLaunchPanel
            key={props.composerProps.activeThreadId}
            {...props.composerProps}
            showAdmission={false}
          />
          {props.usageNotice ? <ComposerBannerStack items={[props.usageNotice]} /> : null}
          {props.queuedMessages && props.onSteerQueuedMessage && props.onRemoveQueuedMessage ? (
            <QueuedComposerMessages
              queuedMessages={props.queuedMessages}
              onSteerQueuedMessage={props.onSteerQueuedMessage}
              onRemoveQueuedMessage={props.onRemoveQueuedMessage}
            />
          ) : null}
          <ChatComposer {...props.composerProps} />
        </div>
      )}
    </div>
  );
}
